import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import ShoppingPreferencesForm, {
  ShoppingPreferencesFormValues,
} from '../../components/common/Forms/ShoppingPreferencesForm';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import useApi, { Status } from '../../common/hooks/useApi';
import { GetProfile, UpdateProfile } from '../../services/profile/ProfileService';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import { GetUserInterests, UpdateUserInterests } from '../../services/interests/InterestService';
import { createUpdateProfilePayload } from '../../services/profile/UpdateProfilePayload';
import { createUpdateUserInterestsPayload } from '../../services/interests/UpdateUserInterestsPayload';
import { formatDateToISO } from '../../components/common/FieldFormat';
import { Gender } from 'services/profile/constants';
import { useLoading } from './PreferencesBase';
import { Helmet } from 'react-helmet';
import { SyncAccount } from 'components/common/syncAccount';

const ShoppingPage = (): JSX.Element => {
  const { t } = useTranslation('component');
  const { user } = useAuth0();
  const [fetchProfile, profile, fetchingProfileStatus] = useApi(GetProfile);
  const [fetchUserInterests, userInterests, fetchingUserInterestsStatus] = useApi(GetUserInterests);
  const [shoppingPreferences, setShoppingPreferences] = useState<ShoppingPreferencesFormValues | null>(null);
  const [saveMessage, setSaveMessage] = useState({ message: '', type: '' });
  const { isLoading, setIsLoading } = useLoading();
  const [showPtsBox, setShowPtsBox] = useState<boolean>(false);

  useEffect(() => {
    // Fetch user data when component mounts
    fetchProfile(user?.session_id);
    fetchUserInterests(user?.session_id);
  }, [fetchProfile, fetchUserInterests, user?.session_id]);

  useEffect(() => {
    // Initialize form values with the results of the user data api calls
    if (profile && userInterests) {
      setShowPtsBox(profile?.Card && profile?.Card?.BonusStillToGet?.ProfileOptionalInfoBonus > 0);
      const birthdayDate =
        profile.BirthDateYear && profile.BirthDateMonth && profile.BirthDateDay
          ? formatDateToISO(new Date(`${profile.BirthDateYear}/${profile.BirthDateMonth}/${profile.BirthDateDay}`))
          : null;
      setShoppingPreferences({
        InvoicePreference: profile.EReceiptPreference,
        GenderPreference: profile.Gender == undefined ? Gender.Unknown : profile.Gender,
        BirthdayDate: birthdayDate,
      });
    }
  }, [profile, userInterests]);

  useEffect(() => {
    setIsLoading(fetchingProfileStatus === Status.Pending || fetchingUserInterestsStatus === Status.Pending);
  }, [fetchingProfileStatus, fetchingUserInterestsStatus, setIsLoading]);

  const isFamiliplusMember = !!profile?.Card?.CardNumber;
  const hasFetchDataFailed =
    fetchingProfileStatus === Status.Rejected || fetchingUserInterestsStatus === Status.Rejected;

  const handleSave = async function (values: ShoppingPreferencesFormValues) {
    if (!isLoading) {
      setIsLoading(true);
      setSaveMessage({ message: '', type: '' });
      const updatePromises = [];
      const updateProfilePayload = createUpdateProfilePayload(profile);
      updateProfilePayload.Gender =
        values.GenderPreference || values.GenderPreference == 0 ? values.GenderPreference : Gender.Unknown;
      updateProfilePayload.EReceiptPreference = values.InvoicePreference;
      if (values.BirthdayDate) {
        const [year, month, day] = values.BirthdayDate.split('-');
        updateProfilePayload.BirthDateYear = +year;
        updateProfilePayload.BirthDateMonth = +month;
        updateProfilePayload.BirthDateDay = +day;
      }
      updatePromises.push(UpdateProfile(user!.session_id, updateProfilePayload));

      const updateUserInterestsPayload = createUpdateUserInterestsPayload(userInterests);
      updateUserInterestsPayload.NoChild = true;
      updateUserInterestsPayload.ChildrenYearOfBirth = [];

      updatePromises.push(UpdateUserInterests(user!.session_id, updateUserInterestsPayload));
      try {
        await Promise.all(updatePromises);
        setSaveMessage({ message: t('Preferences.SaveSuccess') as string, type: 'success' });
        SyncAccount();
      } catch (e) {
        setSaveMessage({ message: t('Errors.GenericError') as string, type: 'error' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.ShoppingPrefTitle')}</title>
        <meta name="description" content={t('MetaTag.ShoppingPrefDescription').toString()} />
      </Helmet>
      {hasFetchDataFailed && (
        <Alert severity="error" sx={{ mb: '16px' }}>
          {t('Preferences.FetchDataFailed')}
        </Alert>
      )}
      {saveMessage.type === 'success' && (
        <Alert severity="success" sx={{ mb: '16px' }}>
          {saveMessage.message}
        </Alert>
      )}

      <Typography mb={'2em'}>{t('Preferences.ShoppingDescription')}</Typography>
      {!shoppingPreferences && isLoading && <LoadingIndicator />}
      {shoppingPreferences && (
        <ShoppingPreferencesForm
          shoppingPreferences={shoppingPreferences}
          isFamiliplusMember={isFamiliplusMember}
          saveHandler={handleSave}
          saveErrorMessage={saveMessage.type === 'error' ? saveMessage.message : null}
          footer={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'right' }}>
              {isLoading && <LoadingIndicator sx={{ margin: '0 16px 0 0' }} />}
              <Button type="submit" variant="contained" disabled={isLoading}>
                {t('Buttons.Save')}
              </Button>
            </Box>
          }
          showPtsBox={showPtsBox}
        />
      )}
    </>
  );
};

export default ShoppingPage;
